import {startTransition, useCallback, useLayoutEffect, useState} from 'react';
import type {EmblaCarouselType} from 'embla-carousel';

import {usePrefersReducedMotion} from '@/hooks/usePrefersReducedMotion';

type UsePrevNextButtonsType = {
	prevBtnDisabled: boolean;
	nextBtnDisabled: boolean;
	onPrevButtonClick: () => void;
	onNextButtonClick: () => void;
};

export const usePrevNextButtons = (
	emblaApi: EmblaCarouselType | undefined,
): UsePrevNextButtonsType => {
	const prefersReducedMotion = usePrefersReducedMotion();
	const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
	const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

	const onPrevButtonClick = useCallback(() => {
		if (!emblaApi) {
			return;
		}
		emblaApi.scrollPrev(prefersReducedMotion);
	}, [emblaApi, prefersReducedMotion]);

	const onNextButtonClick = useCallback(() => {
		if (!emblaApi) {
			return;
		}
		emblaApi.scrollNext(prefersReducedMotion);
	}, [emblaApi, prefersReducedMotion]);

	const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
		setPrevBtnDisabled(!emblaApi.canScrollPrev());
		setNextBtnDisabled(!emblaApi.canScrollNext());
	}, []);

	useLayoutEffect(() => {
		if (!emblaApi) {
			return;
		}

		startTransition(() => {
			onSelect(emblaApi);
		});
		emblaApi.on('reInit', onSelect).on('select', onSelect);

		return () => {
			emblaApi.off('reInit', onSelect).off('select', onSelect);
		};
	}, [emblaApi, onSelect]);

	return {
		prevBtnDisabled,
		nextBtnDisabled,
		onPrevButtonClick,
		onNextButtonClick,
	};
};
